import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, getDoc, setDoc, addDoc, collection } from 'firebase/firestore';
import { auth, db } from './firebase';
import { TENANT_CONFIG } from './config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userSetupComplete, setUserSetupComplete] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [autowatchServerUrl, setAutowatchServerUrl] = useState(null);

  // Function to load config from Firebase
  const loadConfigFromFirebase = async () => {
    setConfigLoading(true);
    try {
      const configDocRef = doc(db, 'inspectConfig', 'default');
      const configDoc = await getDoc(configDocRef);
      
      if (configDoc.exists()) {
        const configData = configDoc.data();
        const serverUrl = configData.AUTOWATCH_SERVER_URL || TENANT_CONFIG.autowatch?.serverUrl || 'https://api.threeclicks.ai';
        setAutowatchServerUrl(serverUrl);
      } else {
        // Fallback to hardcoded config
        const serverUrl = TENANT_CONFIG.autowatch?.serverUrl || 'https://api.threeclicks.ai';
        setAutowatchServerUrl(serverUrl);
      }
    } catch (error) {
      // Fallback to hardcoded config
      const serverUrl = TENANT_CONFIG.autowatch?.serverUrl || 'https://api.threeclicks.ai';
      setAutowatchServerUrl(serverUrl);
    } finally {
      setConfigLoading(false);
    }
  };

  // Function to ensure user setup is complete
  const ensureUserSetup = async (user) => {
    try {
      // 1. Check if User document exists
      const userDocRef = doc(db, 'user', user.email); // Use email as document ID
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.log('Creating user setup for:', user.email);
        
        // 2. Create User document
        await setDoc(userDocRef, {
          UserId: user.email, // Use email as UserId
          EmailId: user.email,
          Name: user.displayName || user.email,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        // 3. Create Company document
        const companyData = {
          Name: `${user.displayName || user.email.split('@')[0]}'s Company`,
          CreatedBy: user.email, // Use email instead of uid
          createdAt: new Date().toISOString()
        };
        const companyDocRef = await addDoc(collection(db, 'company'), companyData);
        const companyId = companyDocRef.id;
        console.log('Company created with ID:', companyId);
        
        // 4. Create CompanyUser relationship
        const companyUserDocRef = doc(db, 'companyUser', `${user.email}_${companyId}`); // Use email instead of uid
        console.log('Creating companyUser with document ID:', `${user.email}_${companyId}`);
        await setDoc(companyUserDocRef, {
          UserId: user.email, // Use email as UserId
          CompanyId: companyId,
          Role: 'admin', // First user becomes admin
          createdAt: new Date().toISOString()
        });
        console.log('CompanyUser created successfully');
      }
      
      setUserSetupComplete(true);
    } catch (error) {
      console.error('Error during user setup:', error);
      // Don't set userSetupComplete to true if setup fails
    }
  };

  function logout() {
    setUserSetupComplete(false); // Reset setup state on logout
    return signOut(auth);
  }

  function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'  // Forces Google to show account picker
    });
    return signInWithPopup(auth, provider);
  }

  function loginWithMicrosoft() {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      prompt: 'select_account'  // Forces Microsoft to show account picker
    });
    return signInWithPopup(auth, provider);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Ensure user setup is complete
        if (!userSetupComplete) {
          await ensureUserSetup(user);
        }
        // Load config from Firebase after user setup
        await loadConfigFromFirebase();
      } else {
        // Reset setup state when user logs out
        setUserSetupComplete(false);
        setAutowatchServerUrl(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [userSetupComplete]);

  const value = {
    currentUser,
    logout,
    loginWithGoogle,
    loginWithMicrosoft,
    userSetupComplete,
    loading,
    configLoading,
    autowatchServerUrl
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 